import React, { FunctionComponent, useEffect, useState } from 'react';
import WhiteCard from 'global/atoms/WhiteCard';
import { IGetLocations, ILocations } from 'utils/models';
import LocationsConnection from 'utils/connections/locations';
import { getLocationsState } from 'utils/states';
import Pagination from 'global/pagination/Pagination';
import Filters from 'global/filters/Filters';
import Input from 'global/atoms/Input';
import Select from 'global/atoms/Select';
import { Cell, Column, HeaderCell } from 'rsuite-table';
import settings from 'assets/svg/settings.svg';
import List from 'global/table/List';
import { useHistory } from 'react-router-dom';
import arrow from 'assets/svg/arrow_down_small.svg';
import { useDispatch } from 'react-redux';
import HeaderButtons from 'global/atoms/headerButtons/HeaderButtons';
import ButtonFilters from 'global/atoms/ButtonFilters';
import ButtonAddNew from 'global/atoms/ButtonAddNew';
import Spinner from 'global/atoms/Spinner/Spinner';
import ActionsContainer from '../../../../global/atoms/ActionsContainer';
import {
  DeleteSelectedButton,
  DeleteSingleButton,
  ToggleAllCheckbox,
  ToggleSingleCheckbox,
} from '../../../../utils/toggleToDeleteHelper';
import { deepClone, handleToast } from '../../../../utils/helpers';
import ButtonListRedirect from '../../../../global/atoms/ButtonListRedirect';
import { handleClearFilters, loadFilters } from '../../../../utils/filterHelper';
import FiltersStorage from '../../../../utils/FiltersStorage';

interface ILocationsList {}

const LocationsList: FunctionComponent<ILocationsList> = () => {
  const [data, setData] = useState<ILocations | null | 'error'>(null);
  const defaultFilters = deepClone(getLocationsState);
  const [filters, setFilters] = useState<IGetLocations>(loadFilters(defaultFilters));
  const [toggleFilters, setToggleFilters] = useState<boolean>(false);
  const [filtersForm, setFiltersForm] = useState<any>('loading');
  const history = useHistory();
  const dispatch = useDispatch();
  const [chosenToDelete, setChosenToDelete] = useState<Array<string>>([]);

  useEffect(() => {
    LocationsConnection.getFormFilterLocations().then((data) => {
      setFiltersForm(data.data);
      data.data?.header && dispatch({ type: 'SET_HEADER', payload: data.data.header });
    });
  }, []);

  const reloadData = () => {
    LocationsConnection.getLocations(filters).then((data) => {
      setData(data.data);
      data.data?.header && dispatch({ type: 'SET_HEADER', payload: data.data.header });
    });
  };

  useEffect(() => {
    setFilters((state: IGetLocations) => ({
      ...state,
      requestPaginate: {
        page: 1,
        limit: state.requestPaginate.limit,
      },
    }));

    reloadData();
  }, [filters.requestPaginate.limit]);

  useEffect(() => {
    reloadData();
  }, [filters.requestPaginate.page, filters.requestOrder]);

  const handleToggleSort = (event: any) => {
      if (filters.requestOrder.field === event.currentTarget.dataset.field) {
        setFilters({
          ...filters,
          requestOrder: {
            field: filters.requestOrder.field,
            order: filters.requestOrder.order === 'asc' ? 'desc' : 'asc',
          },
        });
      } else {
        setFilters({
          ...filters,
          requestOrder: {
            field: event.currentTarget.dataset.field,
            order: 'asc',
          },
        });
      }
    },
    handleToggleFilters = () => setToggleFilters((state: boolean) => !state),
    handleSubmitFilters = (event: any) => {
      event.preventDefault();

      const filtersStorage = new FiltersStorage();
      filtersStorage.save(filters);

      LocationsConnection.getLocations({
        ...filters,
        requestPaginate: {
          page: 1,
          limit: filters.requestPaginate.limit,
        },
      }).then((data) => {
        setToggleFilters(false);
        setData(data.data);
        data.data?.header && dispatch({ type: 'SET_HEADER', payload: data.data.header });
        setFilters((state: IGetLocations) => ({
          ...state,
          requestPaginate: { page: 1, limit: state.requestPaginate.limit },
        }));
      });
    },
    handleEdit = (event: any) => {
      history.push(`/datasets/locations/${event.currentTarget.dataset.id}/edit`);
    };

  if (data === null || filtersForm === 'loading') return <Spinner />;

  if (data === 'error') return <div>Błąd pobierania danych</div>;

  const handleDeleteSelected = (deleteId: string) => {
    let toDelete = chosenToDelete.map((id) => ({ id: id }));
    if (deleteId !== undefined) {
      toDelete = [{ id: deleteId }];
    }
    handleToast(LocationsConnection.batchDelete(toDelete)).then(() => {
      setChosenToDelete([]);
      reloadData();
    });
  };

  return (
    <>
      <HeaderButtons>
        <ButtonFilters onClick={handleToggleFilters} />
        <ButtonAddNew form={filtersForm} />
        <DeleteSelectedButton chosenToDelete={chosenToDelete} callback={handleDeleteSelected} form={filtersForm} />
      </HeaderButtons>
      <WhiteCard mobileTransparent={true} padding={false}>
        <List data={data.data} count={data.data.length}>
          <Column width={40} align="left">
            <HeaderCell depth={0} width={0}>
              <ToggleAllCheckbox data={data.data} state={chosenToDelete} setState={setChosenToDelete} />
            </HeaderCell>
            <Cell width={100} depth={0}>
              {(rowData) => (
                <ToggleSingleCheckbox state={chosenToDelete} setState={setChosenToDelete} selectedId={rowData.id} />
              )}
            </Cell>
          </Column>
          <Column flexGrow={1} minWidth={300} align="left">
            <HeaderCell depth={0} width={0}>
              <span
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                onClick={handleToggleSort}
                data-field={'name'}
              >
                Nazwa
                {filters.requestOrder.field === 'name' &&
                  (filters.requestOrder.order === 'asc' ? (
                    <img src={arrow} alt={'sort'} />
                  ) : (
                    <img src={arrow} alt={'sort'} style={{ transform: 'rotate(180deg)' }} />
                  ))}
              </span>
            </HeaderCell>
            <Cell width={0} depth={0}>
              {(rowData) => (
                <ButtonListRedirect redirectUrl={`/datasets/locations/${rowData.id}/edit`}>
                  {rowData.name}
                </ButtonListRedirect>
              )}
            </Cell>
          </Column>
          <Column flexGrow={1} minWidth={200} align="left">
            <HeaderCell depth={0} width={0}>
              Adres
            </HeaderCell>
            <Cell width={0} depth={0} dataKey="name">
              {(rowData) => (
                <span>
                  {rowData.address.street.name
                    ? `${rowData.address.street.name} ${rowData.address.houseNumber}${
                        rowData.address.apartmentNumber ? '/' + rowData.address.apartmentNumber : ''
                      }, ${rowData.address.postCode.name} ${rowData.address.city.name}`
                    : ''}
                </span>
              )}
            </Cell>
          </Column>
          <Column width={150} align="left">
            <HeaderCell depth={0} width={0}>
              Sieć
            </HeaderCell>
            <Cell width={0} depth={0}>
              {(rowData) => <span>{rowData.network.name}</span>}
            </Cell>
          </Column>
          <Column width={150} align="left">
            <HeaderCell depth={0} width={0}>
              Miasto
            </HeaderCell>
            <Cell width={0} depth={0}>
              {(rowData) => <span>{rowData.address.city?.name}</span>}
            </Cell>
          </Column>
          <Column width={100} align="left">
            <HeaderCell depth={0} width={0}>
              Typ lokalizacji
            </HeaderCell>
            <Cell width={0} depth={0}>
              {(rowData) => <span>{rowData.locationType?.name}</span>}
            </Cell>
          </Column>
          <Column verticalAlign={'middle'} align="right" fixed={'right'}>
            <HeaderCell depth={0} width={0}>
              Akcje
            </HeaderCell>
            <Cell depth={0} width={0}>
              {(rowData) => (
                <>
                  <ActionsContainer>
                    <img
                      src={settings}
                      style={{ cursor: 'pointer' }}
                      alt={'Idź do'}
                      data-id={rowData.id}
                      onClick={handleEdit}
                    />
                    <DeleteSingleButton callback={handleDeleteSelected.bind(null, rowData.id)} form={filtersForm} />
                  </ActionsContainer>
                </>
              )}
            </Cell>
          </Column>
        </List>
      </WhiteCard>
      <Pagination
        count={data.count}
        limit={filters.requestPaginate.limit.toString()}
        page={filters.requestPaginate.page.toString()}
        setState={setFilters}
      />
      <Filters
        toggleFilters={toggleFilters}
        handleToggleFilters={handleToggleFilters}
        handleSubmit={handleSubmitFilters}
        handleClear={handleClearFilters.bind(null, setFilters, defaultFilters, reloadData)}
      >
        <Input
          value={filters.name}
          placeholder={'Nazwa'}
          name={'name'}
          type={'text'}
          setState={setFilters}
          state={filters}
        />
        <Input
          value={filters.description}
          placeholder={'Opis'}
          name={'description'}
          type={'text'}
          setState={setFilters}
          state={filters}
        />
        <Input value={filters.nip} placeholder={'Nip'} name={'nip'} setState={setFilters} state={filters} />
        <Input value={filters.street} placeholder={'Ulica'} name={'street'} setState={setFilters} state={filters} />
        <Input value={filters.city} placeholder={'Miasto'} name={'city'} setState={setFilters} state={filters} />
        <Input
          value={filters.community}
          placeholder={'Gmina'}
          name={'community'}
          setState={setFilters}
          state={filters}
        />
        <Input
          value={filters.district}
          placeholder={'Powiat'}
          name={'district'}
          setState={setFilters}
          state={filters}
        />
        <Select
          placeholder={'Województwo'}
          name={'voivodeshipId'}
          value={filters.voivodeshipId}
          options={filtersForm.voivodeships.options}
          disabled={false}
          state={filters}
          setState={setFilters}
        />
        <Select
          placeholder={'Sieć'}
          name={'networkId'}
          value={filters.networkId}
          options={filtersForm.networks.options}
          disabled={false}
          state={filters}
          setState={setFilters}
        />
      </Filters>
    </>
  );
};

export default LocationsList;
